import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { Form } from 'react-bootstrap';
import store from 'store';
import { dataActions } from 'reducers';
import { CartGroup, Product, ProductInLocalCart, Unit } from 'lib/types';
import { getCurrencyFormat, LG } from 'lib/util';
import './ProductSelection.scss';
import { cartMethods, productMethods } from 'business_logic';
import NumberInput from 'components/NumberInput';
import ButtonComponent from 'components/ButtonComponent';
import useCallbackState from 'lib/hooks/useCallbackState';
import { useNotification } from 'components/NotificationComponent/NotificationComponent';
import { GA_addProductsToCart } from 'lib/gtm-utils';
import useCartMessage from 'lib/hooks/useCartMessage';
import AdditionsDisplay from './AdditionsDisplay';
import { Messages } from 'reducers/data';
import { useDevice } from 'lib/hooks/useDevice';

interface ProductSelectionProps {
    selectedProduct: Product;
    inputRef?: React.RefObject<HTMLInputElement>;
}

const ProductSelection = ({ selectedProduct, inputRef }: ProductSelectionProps) => {
    const { isDesktop } = useDevice();
    const { setShowCartMessage } = useCartMessage();

    const showNotification = useNotification();

    const productDefaultDimension = Number(selectedProduct.dimensionDefault ?? 1);
    const [productDimension, setProductDimension] = useState(productDefaultDimension);
    const [selectedAdditions, setSelectedAdditions] = useState(Array<boolean>());
    const [productQuantity, setProductQuantity] = useCallbackState<number>(1);

    const [correctAdditions, setCorrectAdditions] = useState(true);
    const [correctQuantity, setCorrectQuantity] = useState(true);
    const [resetNumberInputAdditions, setResetNumberInputAdditions] = useState(false);
    const [resetNumberInputQuantity, setResetNumberInputQuantity] = useState(false);

    const [selectedUnit, setSelectedUnit] = useState<Unit>({ unit: 'szt', isDefault: false, converter: 1, description: 'sztuki' });

    const handleUnitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const unit = selectedProduct.priceInfo.units[parseInt(e.currentTarget.value)];

        setSelectedUnit(unit);
    };

    const mapSelectedProductDataToProductInLocalCart = (): ProductInLocalCart => ({
        product: selectedProduct,
        dimension: productDimension,
        quantity: productQuantity,
        additionsSelected: selectedAdditions,
        mainProductSymkar: null,
        unit: selectedUnit,
        cartGroup: CartGroup.main,
        systemData: null
    });

    const addProductSubmit = () => {
        if (productQuantity > 0 && (productDimension ?? 1) > 0 && selectedUnit) {
            if (isDesktop) {
                setShowCartMessage(Messages.cartMessage);
                inputRef?.current?.select();
            } else showNotification('Dodano produkt do koszyka');
            const product: ProductInLocalCart = mapSelectedProductDataToProductInLocalCart();
            store.dispatch(dataActions.addToLocalCart({ product }));
            GA_addProductsToCart([product], 'assortment');
            setProductQuantity(1);
            setProductDimension(productDefaultDimension);
            setResetNumberInputAdditions(true);
            setResetNumberInputQuantity(true);
        }
    };

    useEffect(() => {
        setProductDimension(productDefaultDimension);
        setProductQuantity(1, () => inputRef?.current?.select());
        setSelectedAdditions(_.map(selectedProduct?.additions, (addition) => addition.default ?? false));
        setSelectedUnit(cartMethods.getDefaultUnitOfProduct(selectedProduct));
        setCorrectQuantity(true);
        setCorrectAdditions(true);
    }, [selectedProduct, productDefaultDimension]);

    const PriceDisplay = () => {
        const hasConverterMB = (product: Product) => product.converterMB > 0;
        const productInCartData = mapSelectedProductDataToProductInLocalCart();
        const productPriceData = cartMethods.getProductPriceData(productInCartData);

        return (
            <div className='ProductSelection-addProduct-priceDisplay'>
                <div className='ProductSelection-addProduct-priceDisplay-rowTitle'>
                    {hasConverterMB(selectedProduct) ? 'Ilość w metrach bieżących:' : `Ilość ${selectedProduct.unit}.:`}
                </div>
                <div className='ProductSelection-addProduct-priceDisplay-price'>
                    {cartMethods.getTotalQuantityInDefaultUnit(productInCartData).toFixed(4)}
                </div>
                {(selectedProduct.converterM2 > 0) ?
                    <>
                        <div className='ProductSelection-addProduct-priceDisplay-rowTitle'>
                            {productMethods.getProductUnit2MDescription(selectedProduct)}
                        </div>
                        <div className='ProductSelection-addProduct-priceDisplay-price'>{cartMethods.getTotalQuantityInM2(productInCartData).toFixed(4)}</div>
                    </> : null}
                <div className='ProductSelection-addProduct-priceDisplay-rowTitle'>
                    Wartość brutto:
                </div>
                <div className='ProductSelection-addProduct-priceDisplay-price'>
                    {getCurrencyFormat(productPriceData.gross)}
                </div>
                <div className='ProductSelection-addProduct-priceDisplay-rowTitle' style={{ fontWeight: 'bold' }}>
                    WARTOŚĆ NETTO:
                </div>
                <div className='ProductSelection-addProduct-priceDisplay-price' style={{ fontWeight: 'bold' }}>
                    {getCurrencyFormat(productPriceData.net)}
                </div>
            </div>
        );
    };

    const PiecesToFullPackage = () => {
        const packageUnits: Unit[] = [];
        selectedProduct.priceInfo.units.forEach((u) => {
            if (u.converter !== 1.0) {
                packageUnits.push(u);
            }
        });

        if (packageUnits.length === 0 || selectedUnit.converter !== 1.0) return <div style={{ visibility: 'hidden' }}> <br /> </div>;

        const numberToFullPackage = packageUnits.reduce((acc, u) => {
            if (productQuantity % u.converter === 0) return 0;

            const newV = u.converter - (productQuantity % u.converter);
            if (acc === -1 || newV < acc) {
                return newV;
            }
            return acc;
        }, -1);

        return <div className='ProductSelection-addProduct-piecesToFullPackage' style={numberToFullPackage === 0 ? { visibility: 'hidden' } : {}}>do zbiorczego opakowania: <span style={{ color: 'red' }}>{numberToFullPackage}</span></div>;
    };

    const UnitDisplay = (unit: Unit, index: number, baseUnit: string) => (
        <Form.Group controlId={unit.unit} className='ProductSelection-addProduct-checkBox' key={unit.unit}>
            <Form.Check
                type='radio'
                name={unit.unit}
                value={index}
                checked={unit.unit === selectedUnit.unit}
                onChange={handleUnitChange}
            />
            <Form.Label>{cartMethods.getUnitLabel(unit, baseUnit)}</Form.Label>
        </Form.Group>
    );

    const UnitsDisplay = () => (
        <>
            <div className='NewOrder-typeElem-units'>
                {/* show default unit first */}
                {selectedProduct.priceInfo.units.map((unit, index) => (unit.isDefault ? UnitDisplay(unit, index, selectedProduct.unit) : null))}
                {selectedProduct.priceInfo.units.map((unit, index) => (!unit.isDefault ? UnitDisplay(unit, index, selectedProduct.unit) : null))}
            </div>
            <hr />
        </>
    );

    const PricePerPieceDisplay = () => {
        const productInCartData = mapSelectedProductDataToProductInLocalCart();
        const productPriceData = cartMethods.getProductPriceData(productInCartData);
        if (selectedUnit.unit === 'mb') {
            return (
                <>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'darkblue' }}>
                        {productPriceData.priceAfterDiscountPerSquaredMeter !== Infinity ?
                            <>
                                {`${getCurrencyFormat(productPriceData.priceAfterDiscountPerSquaredMeter)}`} /m<sup>2</sup>
                            </> :
                            <>
                                {getCurrencyFormat(productPriceData.priceAfterDiscountPerDefaultUnit)} /mb
                            </>}
                    </div>
                    <div style={{ lineHeight: '100%', fontSize: '1.1rem' }}>{getCurrencyFormat(productPriceData.priceAfterDiscountPerDefaultUnit)} /mb</div>
                </>
            );
        }

        return (
            <>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'darkblue' }}>
                    {`${getCurrencyFormat(productPriceData.priceAfterDiscountPerDefaultUnit)} /${cartMethods.getUnitShort(cartMethods.getDefaultUnitOfProduct(selectedProduct))}`}
                </div>
            </>
        );
    };

    return isDesktop ? (
        <div className='AssortmentSelection-addProduct-container'>
            <div className='ProductSelection-addProduct ProductSelection-addProduct-desktop'>
                <div className='ProductSelection-addProduct-desktop-header'>
                    <span className='ProductSelection-addProduct-desktop-title'>DOPASUJ PRODUKT</span>
                </div>
                <UnitsDisplay />
                <div className='NewOrder-typeElem' onKeyPress={({ key }) => key === 'Enter' && addProductSubmit()}>
                    <Form.Group controlId='quantity' style={{ width: '33%', margin: '0px' }}>
                        <span>Ilość:</span>
                        <NumberInput
                            value={productQuantity}
                            setValue={(value) => setProductQuantity(value)}
                            minDesktopResolution={LG}
                            disabledEdit={productQuantity === 1}
                            inputRef={inputRef}
                            correct={correctQuantity}
                            setCorrect={setCorrectQuantity}
                            defaultValue={1}
                            reset={resetNumberInputQuantity}
                            setReset={setResetNumberInputQuantity}
                        />
                        <PiecesToFullPackage />
                    </Form.Group>
                    <Form.Group controlId='dimension' style={{ width: '33%', margin: '0px' }}>
                        <>
                            <span>Wymiar: </span>
                            <NumberInput
                                value={productDimension}
                                setValue={(value) => setProductDimension(value)}
                                step={0.1}
                                min={selectedProduct.dimensionMin ?? 0.01}
                                max={selectedProduct.dimensionMax ?? 10 ** 5}
                                fractDigits={3}
                                disabled={!selectedProduct.dimensionCanEdit}
                                minDesktopResolution={LG}
                                disabledEdit={productDimension === 0.01}
                                correct={correctAdditions}
                                setCorrect={setCorrectAdditions}
                                defaultValue={productDefaultDimension}
                                reset={resetNumberInputAdditions}
                                setReset={setResetNumberInputAdditions}
                                showMinMaxOnInvalidRange
                            />
                        </>
                    </Form.Group>
                    <Form.Group style={{ width: '33%', margin: '0px' }}>
                        <span style={{ fontWeight: 'bold' }}>Cena po rabacie: </span>
                        <PricePerPieceDisplay />
                    </Form.Group>
                </div>
                <hr />
                <AdditionsDisplay
                    selectedProduct={selectedProduct}
                    selectedAdditions={selectedAdditions}
                    setSelectedAdditions={setSelectedAdditions}
                />
                <PriceDisplay />
                <div className='ProductSelection-addToCart-mobile' style={{ justifyContent: 'flex-end' }}>
                    <ButtonComponent
                        text='DODAJ DO KOSZYKA'
                        onClick={addProductSubmit}
                        marginTop='0px'
                        variant='success'
                        brandColoredButton={false}
                        disabled={!(correctQuantity && correctAdditions)}
                    />
                </div>
            </div>
        </div>
    ) : (
        <div>
            <div className='ProductSelection-addProduct'>
                <div className='ProductSelection-addProductHeader'>
                    <b>{selectedProduct.description}</b>
                </div>
                <UnitsDisplay />
                <div className='NewOrder-typeElem'>
                    <Form.Group controlId='quantity' style={{ margin: '0 3%' }}>
                        <span>Ilość sztuk: </span>
                        <NumberInput
                            value={productQuantity}
                            setValue={(value) => setProductQuantity(value)}
                            minDesktopResolution={LG}
                            disabledEdit={productQuantity === 1}
                            correct={correctQuantity}
                            setCorrect={setCorrectQuantity}
                            defaultValue={1}
                            reset={resetNumberInputQuantity}
                            setReset={setResetNumberInputQuantity}
                        />
                        <PiecesToFullPackage />
                    </Form.Group>
                    <Form.Group controlId='dimension' style={{ margin: '0 3%', marginBottom: '5px' }}>
                        <span>Wymiar: </span>
                        <NumberInput
                            value={productDimension}
                            setValue={(value) => setProductDimension(value)}
                            step={0.1}
                            min={selectedProduct.dimensionMin ?? 0.01}
                            max={selectedProduct.dimensionMax ?? 10 ** 5}
                            fractDigits={3}
                            disabled={!selectedProduct.dimensionCanEdit}
                            minDesktopResolution={LG}
                            disabledEdit={productDimension === 0.01}
                            correct={correctAdditions}
                            setCorrect={setCorrectAdditions}
                            defaultValue={productDefaultDimension}
                            reset={resetNumberInputAdditions}
                            setReset={setResetNumberInputAdditions}
                        />
                    </Form.Group>
                    <Form.Group style={{ margin: '0 3%', marginBottom: '5px' }}>
                        <span style={{ fontWeight: 'bold' }}>Cena po rabacie: </span>
                        <PricePerPieceDisplay />
                    </Form.Group>
                </div>
                <hr />
                <AdditionsDisplay
                    selectedProduct={selectedProduct}
                    selectedAdditions={selectedAdditions}
                    setSelectedAdditions={setSelectedAdditions}
                />
                <PriceDisplay />
            </div>
            <div className='ProductSelection-addToCart-mobile'>
                <ButtonComponent
                    text='DODAJ DO KOSZYKA'
                    marginBottom='0px'
                    variant='success'
                    brandColoredButton={false}
                    onClick={addProductSubmit}
                    disabled={!(correctQuantity && correctAdditions)}
                />
            </div>
        </div>
    );
};

export default ProductSelection;
